import { useRef, useState } from "react";
import { Upload, Loader2, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MODULE_HINTS = {
    medicines: "Columns: name, generic, form, strength, specialty, usage",
    pharmacy: "Columns: name, batch, expiry, unit, qty, purchase_rate, sale_rate, gst_rate, reorder_level",
    patients: "Columns: name, phone, age, gender, address, blood_group, allergies, referred_by",
    ipd: "Columns: patient_uid, admission_date, ward, reason, provisional_diagnosis",
    nabh: "Columns: month (YYYY-MM), key, label, value",
    compliance: "Columns: entry_type (MTP/PCPNDT), date, patient_name, doctor_name, indication, gestation_weeks, method, form_no, consent_obtained (yes/no), remarks",
    appointments: "Columns: patient_uid, date (YYYY-MM-DD), time (HH:MM), reason, doctor (name)",
};

export default function ImportDialog({ module, label = "Import", storeAuth = false, onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);
    const isDocs = module === "documents";

    const authHeaders = () => {
        const h = {};
        if (storeAuth) {
            const t = localStorage.getItem("store_token");
            if (t) h.Authorization = `Bearer ${t}`;
        }
        return h;
    };

    const downloadTemplate = async () => {
        try {
            const res = await api.get(`/import/${module}/template`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${module}-import-template.csv`;
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const submit = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            if (isDocs) files.forEach((f) => fd.append("files", f));
            else fd.append("file", files[0]);
            const { data } = await api.post(isDocs ? "/documents/bulk-upload" : `/import/${module}`, fd, { headers: authHeaders() });
            setResult(data);
            if (isDocs) toast.success(`${data.assigned}/${data.total} files auto-assigned to patients`);
            else toast.success(`Imported ${data.inserted}/${data.total_rows} rows`);
            onDone && onDone();
        } catch (err) {
            toast.error(fmtErr(err));
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };

    return (
        <>
            <Button data-testid={`import-${module}-btn`} variant="outline" onClick={() => { setResult(null); setOpen(true); }} className="gap-2">
                <Upload className="h-4 w-4" /> {label}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid={`import-${module}-dialog`}>
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#E06D1F]" /> {isDocs ? "Bulk Document Auto-Assign" : `Import — ${label}`}</DialogTitle>
                        <DialogDescription>
                            {isDocs
                                ? "Upload up to 20 files. Each filename must contain the Patient ID (e.g. PT-1001_report.pdf) — the file is auto-filed into that patient's Lab Reports."
                                : "Upload a CSV or Excel (.xlsx) file. Rows with errors are skipped and listed below."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {!isDocs && (
                            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                                <p className="text-xs text-slate-500">{MODULE_HINTS[module]}</p>
                                <Button size="sm" variant="ghost" data-testid={`import-template-${module}`} onClick={downloadTemplate} className="gap-1.5 text-xs text-[#C2410C]"><Download className="h-3.5 w-3.5" /> Template</Button>
                            </div>
                        )}
                        <input
                            ref={fileRef}
                            type="file"
                            data-testid={`import-file-${module}`}
                            accept={isDocs ? ".pdf,.jpg,.jpeg,.png,.webp" : ".csv,.xlsx,.xlsm"}
                            multiple={isDocs}
                            onChange={submit}
                            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[#E06D1F] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#B45309] file:cursor-pointer"
                        />
                        {busy && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-[#E06D1F]" /> Importing...</p>}

                        {result && !isDocs && (
                            <div className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid={`import-result-${module}`}>
                                <div className="flex gap-2">
                                    <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">Inserted: {result.inserted}</span>
                                    <span className="rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Skipped: {result.skipped}</span>
                                    <span className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">Total: {result.total_rows}</span>
                                </div>
                                {result.errors?.length > 0 && (
                                    <div className="max-h-40 overflow-y-auto rounded border border-amber-100 bg-amber-50/50 p-2.5 space-y-1" data-testid={`import-errors-${module}`}>
                                        {result.errors.map((er, i) => (
                                            <p key={i} className="text-xs text-amber-800 flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Row {er.row}: {er.error}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {result && isDocs && (
                            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="import-documents-result">
                                {result.results.map((r, i) => (
                                    <p key={i} className="px-3.5 py-2 text-xs flex items-center gap-2">
                                        {r.status === "assigned"
                                            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                            : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                                        <span className="font-medium text-slate-800">{r.file}</span>
                                        <span className="text-slate-500">{r.status === "assigned" ? `→ ${r.patient} (${r.patient_uid})` : `— ${r.reason}`}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
